import React from "react";
import { motion } from "framer-motion";
import {
  Globe2,
  MessageSquareOff,
  MoonStar,
  Wand2,
  Instagram,
  Bot,
  Play,
  Check,
  Plane,
  TrendingUp,
  DollarSign,
  Sparkles,
  Scissors,
  Stethoscope,
  Smile,
  Heart,
  Leaf,
  ArrowRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import FloatingWhatsApp, { WHATSAPP_URL } from "../components/FloatingWhatsApp";
import Logo from "../components/Logo";
import { toast } from "sonner";

// ---------- Reusable ----------
const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const SectionLabel = ({ children, light = false }) => (
  <div
    className={`inline-flex items-center gap-2 text-xs font-inter font-semibold uppercase tracking-[0.18em] ${
      light ? "text-[#C9A84C]" : "text-[#C9A84C]"
    }`}
  >
    <span className="w-6 h-px bg-[#C9A84C]" />
    {children}
  </div>
);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ---------- HERO ----------
function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-[100svh] bg-hero-luxe overflow-hidden flex items-center grain-overlay"
      data-testid="hero-section"
    >
      {/* Background image — aerial night cityscape, heavily darkened */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        <img
          src="https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=2400&q=80"
          alt=""
          className="w-full h-full object-cover opacity-[0.18]"
          style={{ filter: "saturate(0.8) contrast(1.05)" }}
          loading="eager"
        />
        {/* Deep navy wash to keep text legible and set mood */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,26,51,0.82) 0%, rgba(10,35,66,0.92) 55%, rgba(5,15,32,0.98) 100%)",
          }}
        />
        {/* Left vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 80% at 0% 50%, rgba(5,15,32,0.85) 0%, transparent 60%)",
          }}
        />
      </div>

      {/* Warm golden corner glow */}
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#C9A84C]/[0.12] blur-[120px] pointer-events-none animate-pulse-slow" />
      <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#C9A84C]/[0.06] blur-[120px] pointer-events-none" />

      {/* Floating golden dust particles */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <span className="particle" style={{ width: 3, height: 3, top: "22%", left: "18%", animationDelay: "0s" }} />
        <span className="particle" style={{ width: 2, height: 2, top: "38%", left: "78%", animationDelay: "2s" }} />
        <span className="particle" style={{ width: 4, height: 4, top: "62%", left: "12%", animationDelay: "4s" }} />
        <span className="particle" style={{ width: 2, height: 2, top: "74%", left: "88%", animationDelay: "1s" }} />
        <span className="particle" style={{ width: 3, height: 3, top: "14%", left: "55%", animationDelay: "3s" }} />
        <span className="particle" style={{ width: 2, height: 2, top: "86%", left: "42%", animationDelay: "5s" }} />
        <span className="particle" style={{ width: 3, height: 3, top: "46%", left: "92%", animationDelay: "2.5s" }} />
        <span className="particle" style={{ width: 2, height: 2, top: "32%", left: "34%", animationDelay: "6s" }} />
      </div>

      {/* Hairline gold ornament — top-right art-deco arc */}
      <svg
        className="absolute top-24 right-8 md:right-16 w-40 h-40 md:w-56 md:h-56 opacity-40 pointer-events-none"
        viewBox="0 0 200 200"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="98" stroke="#C9A84C" strokeWidth="0.6" strokeDasharray="1 4" />
        <circle cx="100" cy="100" r="70" stroke="#C9A84C" strokeWidth="0.5" opacity="0.5" />
        <path d="M 100 10 A 90 90 0 0 1 190 100" stroke="#C9A84C" strokeWidth="1" fill="none" />
      </svg>

      {/* Hairline gold ornament — bottom-left */}
      <svg
        className="absolute bottom-16 left-0 w-64 h-16 opacity-30 pointer-events-none"
        viewBox="0 0 400 60"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path d="M0 30 H 280" stroke="#C9A84C" strokeWidth="0.8" />
        <circle cx="290" cy="30" r="3" fill="#C9A84C" />
        <path d="M300 30 H 340" stroke="#C9A84C" strokeWidth="0.5" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 md:py-40 w-full z-10">
        <div className="max-w-4xl">
          <motion.div {...fadeInUp}>
            <SectionLabel light>MedBridge · Turismo médico premium</SectionLabel>
          </motion.div>

          <motion.h1
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: 0.1 }}
            className="mt-6 md:mt-7 text-white text-[2rem] sm:text-5xl lg:text-[4.2rem] xl:text-[4.8rem] leading-[1.08] md:leading-[1.04] tracking-[-0.015em] break-words"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 500 }}
            data-testid="hero-headline"
          >
            Tu clínica merece{" "}
            <span
              className="italic text-gradient-gold"
              style={{ fontWeight: 500 }}
            >
              pacientes internacionales
            </span>
          </motion.h1>

          <motion.p
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: 0.2 }}
            className="mt-6 md:mt-8 text-base sm:text-xl text-white/75 font-inter leading-relaxed max-w-2xl"
            data-testid="hero-subtitle"
          >
            Transformamos tu contenido de Instagram al inglés con inteligencia
            artificial, abrimos tu canal para el mercado de Estados Unidos y
            ponemos un asistente virtual que atiende a tus pacientes
            norteamericanos las 24 horas.
          </motion.p>

          <motion.div
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: 0.3 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
          >
            <button
              onClick={() => scrollTo("contacto")}
              className="btn-gold px-7 py-4 rounded-full text-sm sm:text-base font-poppins font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="hero-cta-button"
            >
              Quiero saber más
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => scrollTo("como-funciona")}
              className="px-6 py-4 rounded-full text-sm sm:text-base font-poppins font-medium text-white/80 hover:text-white border border-white/15 hover:border-white/30 transition-colors w-full sm:w-auto"
              data-testid="hero-secondary-button"
            >
              Ver cómo funciona
            </button>
          </motion.div>

          {/* Trust indicators */}
          <motion.div
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: 0.45 }}
            className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-x-10 md:gap-y-4 text-white/50 text-[10px] md:text-xs font-inter uppercase tracking-[0.18em] md:tracking-[0.2em]"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
              AI Lip Sync
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
              Asistente 24/7
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
              Mercado USA
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
              Bogotá, Colombia
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------- PROBLEMA ----------
function Problem() {
  const cards = [
    {
      icon: Globe2,
      title: "La barrera del idioma",
      text: "Miles de estadounidenses buscan tratamientos estéticos en Colombia cada año — pero no encuentran tu clínica porque no hablas su idioma.",
    },
    {
      icon: Instagram,
      title: "Contenido invisible",
      text: "Tu mejor contenido de Instagram nunca llega al público que tiene mayor poder adquisitivo del continente.",
    },
    {
      icon: MoonStar,
      title: "Mensajes a las 2am",
      text: "Cuando un paciente de USA te escribe por Instagram, ¿quién le responde en inglés a las 2 de la mañana?",
    },
  ];

  return (
    <section
      id="problema"
      className="relative bg-white py-20 md:py-28"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel>El problema</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-[#0A2342] text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Estás perdiendo pacientes que ni siquiera sabes que existen
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 font-inter leading-relaxed">
            El turismo médico desde Estados Unidos hacia Colombia crece cada
            año. Tres obstáculos están entre tu clínica y ese mercado.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="group relative bg-white rounded-2xl p-8 border border-slate-200/70 hover:border-[#C9A84C]/40 shadow-[0_4px_24px_-8px_rgba(10,35,66,0.08)] hover:shadow-[0_12px_36px_-8px_rgba(10,35,66,0.12)] transition-all duration-300"
                data-testid={`problem-card-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#0A2342] flex items-center justify-center group-hover:bg-[#C9A84C] transition-colors duration-300">
                  <Icon size={26} className="text-white" strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 font-poppins font-semibold text-xl text-[#0A2342]">
                  {c.title}
                </h3>
                <p className="mt-3 text-slate-600 font-inter leading-relaxed">
                  {c.text}
                </p>
                <span className="absolute top-6 right-6 text-xs font-inter text-slate-300 tabular-nums">
                  0{i + 1}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- CÓMO FUNCIONA ----------
function HowItWorks() {
  const steps = [
    {
      n: "01",
      icon: Wand2,
      title: "Traducimos tu contenido",
      text: "Tomamos tus mejores reels de Instagram y los convertimos al inglés con tecnología de inteligencia artificial, incluyendo lip sync para que parezcas hablar inglés de forma natural y fluida.",
    },
    {
      n: "02",
      icon: Instagram,
      title: "Abrimos tu canal en inglés",
      text: "Creamos y configuramos una cuenta nueva de Instagram dirigida exclusivamente al público anglosajón, con biografía, highlights y estrategia de contenido optimizada para ese mercado.",
    },
    {
      n: "03",
      icon: Bot,
      title: "Activamos tu asistente virtual",
      text: "Instalamos un chatbot inteligente en inglés que responde automáticamente los mensajes de pacientes de USA, resuelve preguntas frecuentes, comparte precios y agenda citas — 24/7.",
    },
  ];

  return (
    <section
      id="como-funciona"
      className="relative bg-[#F5F7FA] py-20 md:py-28 overflow-hidden"
      data-testid="how-it-works-section"
    >
      <div className="absolute inset-0 bg-grid-light opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel>Cómo funciona</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-[#0A2342] text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Tres pasos para conectar tu clínica con Estados Unidos
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.12 }}
                className="relative bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-slate-200/50 shadow-[0_8px_30px_-10px_rgba(10,35,66,0.08)]"
                data-testid={`how-step-${i}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-poppins font-black text-5xl sm:text-6xl lg:text-7xl text-gradient-gold leading-none">
                    {s.n}
                  </span>
                  <div className="w-12 h-12 rounded-xl bg-[#F5F7FA] flex items-center justify-center border border-slate-200/70">
                    <Icon size={22} className="text-[#0A2342]" strokeWidth={1.7} />
                  </div>
                </div>

                <h3 className="mt-8 font-poppins font-semibold text-xl lg:text-2xl text-[#0A2342]">
                  {s.title}
                </h3>
                <p className="mt-3 text-slate-600 font-inter leading-relaxed">
                  {s.text}
                </p>

                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-4 translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-slate-200 items-center justify-center z-10">
                    <ArrowRight size={14} className="text-[#C9A84C]" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- DEMO VIDEO ----------
function DemoVideo() {
  const players = [
    {
      label: "Video original",
      sublabel: "En español",
      flag: "🇨🇴",
      color: "rgba(255,255,255,0.08)",
    },
    {
      label: "Con tecnología MedBridge",
      sublabel: "En inglés · AI Lip Sync",
      flag: "🇺🇸",
      color: "rgba(201,168,76,0.18)",
    },
  ];

  return (
    <section
      id="demo"
      className="relative bg-navy-pattern py-20 md:py-28"
      data-testid="demo-video-section"
    >
      <div className="absolute inset-0 bg-grid-navy opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel light>Demo en video</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-white text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Mira cómo se ve en la práctica
          </h2>
          <p className="mt-5 text-white/60 font-inter text-base sm:text-lg">
            El mismo reel, dos audiencias. Tu voz y tu rostro — hablando inglés
            fluido, con sincronización labial natural.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {players.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="group"
              data-testid={`demo-player-${i}`}
            >
              <div
                className="relative aspect-[9/16] sm:aspect-video rounded-2xl overflow-hidden border border-white/10 cursor-pointer"
                style={{
                  background:
                    "linear-gradient(135deg, #0d2a4f 0%, #081a33 100%)",
                }}
              >
                {/* Abstract phone/reel frame */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="absolute inset-8 rounded-xl"
                    style={{ background: p.color }}
                  />
                  <div className="absolute top-6 left-6 text-xs text-white/60 font-inter">
                    {p.flag} {p.sublabel}
                  </div>
                  <button
                    className="relative w-20 h-20 rounded-full bg-[#C9A84C] flex items-center justify-center text-[#0A2342] transition-transform duration-300 group-hover:scale-110 shadow-[0_10px_40px_-5px_rgba(201,168,76,0.55)]"
                    aria-label="Play demo"
                    data-testid={`demo-play-${i}`}
                  >
                    <Play size={28} fill="#0A2342" />
                  </button>
                </div>
                {/* Wave pattern bottom */}
                <svg
                  className="absolute bottom-4 left-6 right-6 h-8 opacity-60"
                  viewBox="0 0 300 30"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  {Array.from({ length: 40 }).map((_, idx) => (
                    <rect
                      key={idx}
                      x={idx * 7.5}
                      y={15 - Math.abs(Math.sin(idx * 0.8 + i) * 10)}
                      width="3"
                      height={Math.abs(Math.sin(idx * 0.8 + i) * 20) + 4}
                      rx="1.5"
                      fill={i === 0 ? "#ffffff" : "#C9A84C"}
                      opacity="0.7"
                    />
                  ))}
                </svg>
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <p className="font-poppins font-semibold text-white text-lg">
                  {p.label}
                </p>
                <span className="text-xs font-inter uppercase tracking-widest text-white/40">
                  {p.sublabel}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          {...fadeInUp}
          className="mt-10 text-center text-sm text-white/40 font-inter italic"
        >
          Demo de lip sync con inteligencia artificial
        </motion.p>
      </div>
    </section>
  );
}

// ---------- PRECIOS ----------
function Pricing() {
  const setupFeatures = [
    "Traducción y lip sync de 15 reels seleccionados",
    "Creación y configuración completa de cuenta Instagram en inglés",
    "Configuración del asistente virtual en inglés",
    "Estrategia inicial de contenido para mercado anglosajón",
    "30 días de soporte incluido",
  ];

  const monthlyFeatures = [
    "4 a 8 reels nuevos traducidos y publicados mensualmente",
    "Asistente virtual activo 24/7",
    "Reporte mensual de métricas y leads recibidos",
    "Optimización continua del contenido",
    "Soporte prioritario",
  ];

  return (
    <section
      id="precios"
      className="relative bg-white py-20 md:py-28"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel>Servicios y precios</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-[#0A2342] text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Inversión transparente. Retorno medible.
          </h2>
          <p className="mt-5 text-slate-600 font-inter text-base sm:text-lg">
            Dos planes diseñados para que comiences a captar pacientes
            internacionales desde el primer mes.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Setup Card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55 }}
            className="relative bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-slate-200 shadow-[0_8px_30px_-10px_rgba(10,35,66,0.08)]"
            data-testid="pricing-card-setup"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-inter font-semibold uppercase tracking-[0.18em] text-slate-500">
                Setup Inicial
              </span>
              <span className="text-xs font-inter text-slate-400">
                Pago único
              </span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="font-poppins font-bold text-5xl lg:text-6xl text-[#0A2342] tracking-tight">
                $1,500
              </span>
              <span className="text-slate-500 font-inter">USD</span>
            </div>
            <p className="mt-3 text-slate-600 font-inter text-sm">
              Todo lo que necesitas para lanzar tu canal internacional.
            </p>
            <div className="my-8 h-px bg-slate-200" />
            <ul className="space-y-4">
              {setupFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#0A2342]/5 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#0A2342]" strokeWidth={3} />
                  </span>
                  <span className="text-slate-700 font-inter text-sm leading-relaxed">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => scrollTo("contacto")}
              className="mt-10 w-full btn-gold px-6 py-4 rounded-full text-sm font-poppins font-semibold"
              data-testid="pricing-setup-cta"
            >
              Comenzar ahora
            </button>
          </motion.div>

          {/* Monthly Card — Featured */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: 0.12 }}
            className="relative bg-[#0A2342] rounded-2xl p-6 sm:p-8 lg:p-10 border-2 border-[#C9A84C] shadow-[0_20px_60px_-20px_rgba(201,168,76,0.35)]"
            data-testid="pricing-card-monthly"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#0A2342] text-xs font-poppins font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full">
              Recomendado
            </span>
            <div className="flex items-center justify-between">
              <span className="text-xs font-inter font-semibold uppercase tracking-[0.18em] text-[#C9A84C]">
                Plan Mensual
              </span>
              <span className="text-xs font-inter text-white/50">
                Recurrente
              </span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <span className="text-white/60 font-inter text-xl">desde</span>
              <span className="font-poppins font-bold text-5xl lg:text-6xl text-white tracking-tight">
                $400
              </span>
              <span className="text-white/60 font-inter">USD / mes</span>
            </div>
            <p className="mt-3 text-white/60 font-inter text-sm">
              Crecimiento sostenido y atención siempre encendida.
            </p>
            <div className="my-8 h-px bg-white/10" />
            <ul className="space-y-4">
              {monthlyFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#C9A84C] flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#0A2342]" strokeWidth={3} />
                  </span>
                  <span className="text-white/85 font-inter text-sm leading-relaxed">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => scrollTo("contacto")}
              className="mt-10 w-full btn-gold px-6 py-4 rounded-full text-sm font-poppins font-semibold"
              data-testid="pricing-monthly-cta"
            >
              Más información
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------- DATOS ----------
function Stats() {
  const stats = [
    {
      icon: Plane,
      number: "+50,000",
      label: "estadounidenses viajan a Colombia anualmente por turismo médico",
    },
    {
      icon: TrendingUp,
      number: "15%",
      label: "crecimiento anual del mercado de turismo médico desde USA",
    },
    {
      icon: DollarSign,
      number: "$3–8K",
      label: "gasto promedio de un paciente de USA por visita",
    },
    {
      icon: Sparkles,
      number: "1",
      label: "paciente nuevo es suficiente para pagar tu inversión en MedBridge",
    },
  ];

  return (
    <section
      id="datos"
      className="relative bg-[#F5F7FA] py-20 md:py-28"
      data-testid="stats-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel>Por qué funciona</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-[#0A2342] text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Los números que están detrás de la oportunidad
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-[0_4px_20px_-8px_rgba(10,35,66,0.08)]"
                data-testid={`stat-card-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center">
                  <Icon size={18} className="text-[#C9A84C]" strokeWidth={2} />
                </div>
                <div className="mt-6 font-poppins font-black text-5xl lg:text-6xl text-[#0A2342] tracking-tight tabular-nums leading-none">
                  {s.number}
                </div>
                <p className="mt-4 text-slate-600 font-inter text-sm leading-relaxed">
                  {s.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- PARA QUIÉN ----------
function ForWhom() {
  const audiences = [
    { icon: Scissors, label: "Cirujanos plásticos" },
    { icon: Sparkles, label: "Médicos estéticos" },
    { icon: Smile, label: "Odontólogos estéticos" },
    { icon: Stethoscope, label: "Clínicas de medicina estética" },
    { icon: Leaf, label: "Especialistas en cabello y rejuvenecimiento" },
  ];

  return (
    <section
      id="para-quien"
      className="relative bg-white py-20 md:py-28"
      data-testid="for-whom-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <SectionLabel>Para quién</SectionLabel>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-[#0A2342] text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Diseñado para profesionales estéticos de alto nivel
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {audiences.map((a, i) => {
            const Icon = a.icon;
            return (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="group bg-[#F5F7FA] hover:bg-[#0A2342] rounded-2xl p-6 lg:p-8 text-center transition-all duration-300 cursor-default border border-transparent hover:border-[#C9A84C]/30"
                data-testid={`audience-card-${i}`}
              >
                <div className="mx-auto w-14 h-14 rounded-xl bg-white group-hover:bg-[#C9A84C] flex items-center justify-center transition-colors">
                  <Icon
                    size={24}
                    className="text-[#0A2342] group-hover:text-[#0A2342]"
                    strokeWidth={1.6}
                  />
                </div>
                <p className="mt-5 font-poppins font-medium text-sm lg:text-base text-[#0A2342] group-hover:text-white transition-colors leading-snug">
                  {a.label}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- CONTACTO ----------
// Owner WhatsApp for receiving leads (digits only for wa.me)
const OWNER_WHATSAPP = "13056994889";

function Contact() {
  const [form, setForm] = React.useState({
    nombre: "",
    especialidad: "",
    instagram: "",
    whatsapp: "",
  });
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.whatsapp.trim()) {
      toast.error("Completa al menos tu nombre y WhatsApp");
      return;
    }
    setSubmitting(true);

    // Build a WhatsApp message with the lead details
    const lines = [
      "Hola MedBridge, quiero agendar mi llamada gratuita.",
      "",
      `• Nombre: ${form.nombre}`,
      form.especialidad ? `• Especialidad: ${form.especialidad}` : null,
      form.instagram ? `• Instagram: ${form.instagram}` : null,
      `• WhatsApp: ${form.whatsapp}`,
    ].filter(Boolean);
    const message = encodeURIComponent(lines.join("\n"));
    const waUrl = `https://wa.me/${OWNER_WHATSAPP}?text=${message}`;

    // Open WhatsApp in a new tab so the user can send the message to the owner
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setTimeout(() => {
      setSubmitting(false);
      toast.success("¡Listo! Se abrió WhatsApp con tus datos.", {
        description: "Solo toca 'Enviar' y te responderemos en las próximas horas.",
      });
      setForm({ nombre: "", especialidad: "", instagram: "", whatsapp: "" });
    }, 500);
  };

  const inputClass =
    "w-full bg-white/5 border border-white/10 focus:border-[#C9A84C] focus:bg-white/10 rounded-xl px-5 py-4 text-white placeholder:text-white/40 font-inter text-base transition-colors outline-none";

  return (
    <section
      id="contacto"
      className="relative bg-navy-pattern py-20 md:py-28 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 bg-grid-navy opacity-40 pointer-events-none" />
      <div className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full bg-[#C9A84C]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
          <div className="flex justify-center">
            <SectionLabel light>Contacto</SectionLabel>
          </div>
          <h2 className="mt-5 font-poppins font-semibold tracking-tight text-white text-[1.6rem] sm:text-4xl lg:text-5xl leading-[1.15]">
            Agenda una llamada gratuita de 20 minutos
          </h2>
          <p className="mt-5 text-white/65 font-inter text-base sm:text-lg">
            Te mostramos un demo personalizado con tu propio contenido — sin
            costo ni compromiso.
          </p>
        </motion.div>

        <motion.form
          {...fadeInUp}
          transition={{ ...fadeInUp.transition, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="mt-14 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-inter uppercase tracking-[0.15em] text-white/50 mb-2">
                Nombre *
              </label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Dr. / Dra. Nombre Apellido"
                className={inputClass}
                data-testid="contact-input-nombre"
              />
            </div>
            <div>
              <label className="block text-xs font-inter uppercase tracking-[0.15em] text-white/50 mb-2">
                Especialidad médica
              </label>
              <input
                type="text"
                value={form.especialidad}
                onChange={(e) =>
                  setForm({ ...form, especialidad: e.target.value })
                }
                placeholder="Cirugía plástica, odontología estética…"
                className={inputClass}
                data-testid="contact-input-especialidad"
              />
            </div>
            <div>
              <label className="block text-xs font-inter uppercase tracking-[0.15em] text-white/50 mb-2">
                Instagram de tu clínica
              </label>
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                placeholder="@tu_clinica"
                className={inputClass}
                data-testid="contact-input-instagram"
              />
            </div>
            <div>
              <label className="block text-xs font-inter uppercase tracking-[0.15em] text-white/50 mb-2">
                WhatsApp *
              </label>
              <input
                type="tel"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                placeholder="+57 300 000 0000"
                className={inputClass}
                data-testid="contact-input-whatsapp"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full btn-gold px-6 py-4 rounded-full text-sm font-poppins font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {submitting ? "Abriendo WhatsApp…" : "Enviar por WhatsApp"}
            {!submitting && <ArrowRight size={18} />}
          </button>

          <div className="mt-8 flex items-center gap-4">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-xs font-inter uppercase tracking-[0.18em] text-white/40">
              o
            </span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1fba58] text-white font-poppins font-semibold text-sm px-6 py-4 rounded-full transition-colors shadow-[0_10px_30px_-10px_rgba(37,211,102,0.6)]"
            data-testid="contact-whatsapp-button"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M20.52 3.48A11.77 11.77 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.87c0 2.09.55 4.13 1.6 5.93L0 24l6.38-1.67a11.85 11.85 0 0 0 5.66 1.44h.01c6.54 0 11.86-5.32 11.86-11.87 0-3.17-1.24-6.15-3.39-8.42zM12.05 21.8a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.78.99 1-3.68-.23-.38a9.9 9.9 0 0 1-1.52-5.27c0-5.47 4.45-9.92 9.93-9.92 2.65 0 5.14 1.03 7.01 2.91a9.86 9.86 0 0 1 2.9 7.01c0 5.47-4.45 9.93-9.9 9.93z" />
            </svg>
            O escríbenos directamente por WhatsApp
          </a>
        </motion.form>
      </div>
    </section>
  );
}

// ---------- FOOTER ----------
function Footer() {
  return (
    <footer
      className="bg-[#081a33] border-t border-white/5 py-12"
      data-testid="footer-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <Logo variant="light" />
            <p className="mt-3 text-white/40 font-inter text-sm max-w-md">
              Conectamos tu clínica con pacientes de Estados Unidos.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm font-inter text-white/50">
            <a
              href="#privacy"
              className="hover:text-white transition-colors"
              data-testid="footer-privacy"
            >
              Política de privacidad
            </a>
            <a
              href="#terms"
              className="hover:text-white transition-colors"
              data-testid="footer-terms"
            >
              Términos
            </a>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-inter text-white/40">
          <span data-testid="footer-copyright">
            © 2025 MedBridge — Bogotá, Colombia
          </span>
          <span className="uppercase tracking-[0.15em]">
            Hecho con precisión médica
          </span>
        </div>
      </div>
    </footer>
  );
}

// ---------- LANDING ----------
export default function Landing() {
  return (
    <main className="bg-white" data-testid="landing-page">
      <Navbar />
      <Hero />
      <Problem />
      <HowItWorks />
      <DemoVideo />
      <Pricing />
      <Stats />
      <ForWhom />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
